import { useAnimations, useGLTF } from '@react-three/drei';
import { createPortal, useFrame } from '@react-three/fiber';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import {
  BackSide, Box3, Color, Group, Material, Matrix4, Mesh, MeshBasicMaterial, MeshStandardMaterial, Object3D,
  SkinnedMesh,
} from 'three';
import { SkeletonUtils } from 'three-stdlib';
import { DEFAULT_BODY_PATH } from '@/domain/meshMapping';
import { type BodyBounds } from './bodyBounds';
import { GLTFErrorBoundary } from './GLTFErrorBoundary';
import { HeartDualSlashes } from './HeartDualSlashes';
import { HeartSparkBurst } from './HeartSparkBurst';
import { LightningHandOrb } from './LightningHandOrb';
import { MeshAttachment } from './MeshAttachment';
import { toonifyMaterial } from './ToonMaterial';
import { useCharacterAnimation } from './useCharacterAnimation';
import type { CombatAnim, MeshAsset } from './types';

/**
 * Names of the hand bone we attach the lightning orb to. Order matters:
 * - **Bip01-rigs first** (e.g. Cyber Knight from 3DSMax-Biped): the sword-slash
 *   animation moves Bip01_R_Hand, so the orb belongs there.
 * - **Quaternius LEFT-hand variants next**: Punch_Right / Sword_Slash clips
 *   actually animate the model's LEFT arm (model orientation flipped vs viewer),
 *   so visually-right attachment lives on Wrist.L for Quaternius.
 * - Other right-hand fallbacks last.
 */
const HAND_BONE_CANDIDATES = [
  // Bip01 (3DSMax-Biped) — Cyber Knight & similar
  'Bip01_R_Hand', 'bone_weapon_r',
  // Quaternius Modular Men (animations flip visible-right onto Wrist.L)
  'WristL', 'Wrist.L', 'wrist.L',
  'HandL', 'Hand.L', 'hand.L',
  'mixamorigLeftHand',
  // Generic right-hand fallbacks
  'WristR', 'Wrist.R', 'wrist.R',
  'HandR', 'Hand.R', 'hand.R',
  'mixamorigRightHand',
  'Bip01_L_Hand',
];

// DEFAULT_BODY_PATH is now imported from domain/meshMapping for a single source of truth.

/** Track which body paths we've already logged once so re-renders don't spam. */
const loggedBoundsForPath = new Set<string>();

/** A simple fallback humanoid built from primitives — used when no body GLB is present. */
function PlaceholderBody({ onReady }: { onReady: (root: Object3D) => void }) {
  const groupRef = useRef<Group>(null);

  useEffect(() => {
    if (groupRef.current) onReady(groupRef.current);
  }, [onReady]);

  return (
    <group ref={groupRef}>
      {/* Legs */}
      <mesh castShadow position={[0, 0.4, 0]}>
        <boxGeometry args={[0.35, 0.8, 0.25]} />
        <meshStandardMaterial color="#4a5360" />
      </mesh>
      {/* Body / Spine — torso anchor */}
      <group name="Chest" position={[0, 1.0, 0]}>
        <mesh castShadow>
          <boxGeometry args={[0.5, 0.5, 0.3]} />
          <meshStandardMaterial color="#5a6470" />
        </mesh>
        {/* Head — helmet anchor */}
        <group name="Head" position={[0, 0.45, 0]}>
          <mesh castShadow>
            <sphereGeometry args={[0.18, 16, 12]} />
            <meshStandardMaterial color="#d5b89e" />
          </mesh>
        </group>
        {/* Right wrist — weapon anchor (matches Quaternius post-load name "WristR") */}
        <group name="WristR" position={[0.35, 0.05, 0.1]}>
          <mesh castShadow>
            <sphereGeometry args={[0.06, 8, 6]} />
            <meshStandardMaterial color="#d5b89e" />
          </mesh>
        </group>
        {/* Left wrist — secondary anchor */}
        <group name="WristL" position={[-0.35, 0.05, 0.1]}>
          <mesh castShadow>
            <sphereGeometry args={[0.06, 8, 6]} />
            <meshStandardMaterial color="#d5b89e" />
          </mesh>
        </group>
      </group>
    </group>
  );
}

/** Names (case-insensitive substring) of sub-meshes that should be hidden from the player body. */
const WEAPON_LIKE_MESH_NAMES = [
  'pistol', 'gun', 'rifle', 'shotgun', 'firearm',
  'knife', 'dagger', 'sword', 'axe', 'mace', 'hammer',
  'bow', 'arrow', 'staff', 'wand',
];

/**
 * Names whose descendants should NEVER be touched by stripWeapons — these are
 * bones we attach equipment to. If a weapon mesh is added under a wrist or hand
 * bone, the substring filter ('sword', 'bow', etc.) would otherwise also hide
 * the just-attached equipment.
 */
const ATTACHMENT_BONE_NAME_FRAGMENTS = ['wrist', 'hand', 'head'];

function isUnderAttachmentBone(obj: Object3D): boolean {
  let cur: Object3D | null = obj;
  while (cur) {
    const n = (cur.name || '').toLowerCase();
    if (ATTACHMENT_BONE_NAME_FRAGMENTS.some((f) => n.includes(f))) return true;
    cur = cur.parent;
  }
  return false;
}

function GLTFBody({
  bodyPath,
  onReady,
  animation,
  onAnimComplete,
  stripWeapons = false,
  triggerKey = 0,
  bodyEmissive,
  bodyEmissiveIntensity = 0.6,
  bodyColorTint,
  bodyColorTintStrength = 0.6,
}: {
  bodyPath: string;
  onReady: (root: Object3D) => void;
  animation: CombatAnim;
  onAnimComplete?: () => void;
  stripWeapons?: boolean;
  triggerKey?: number;
  /** Hex string — when set, every body sub-material gets this colour as emissive (overrides original). Used for elemental tints (lightning yellow, fire orange). */
  bodyEmissive?: string;
  bodyEmissiveIntensity?: number;
  /** Hex tint LERPed into body submeshes whose base colour is washed-out (lightness > 0.55).
   *  Used to make a Vault-Knight-set body actually look cobalt instead of white when the helm + shield already are. */
  bodyColorTint?: string;
  bodyColorTintStrength?: number;
}) {
  const { scene, animations } = useGLTF(bodyPath);
  // SkeletonUtils.clone properly handles SkinnedMesh.skeleton bone-references.
  const cloned = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { actions, mixer } = useAnimations(animations, cloned);

  useEffect(() => {
    // Track every material we create here so the cleanup can dispose them.
    // Three.js does NOT auto-dispose materials when meshes leave the scene
    // graph; without this, every enemy that mounts/unmounts (room-to-room
    // transitions) leaks ~3-5 toon materials + outline materials, and the
    // GPU shader-program cache grows until the renderer chokes.
    const ownedMaterials: Material[] = [];

    // Pre-pass: count skinned meshes so subsequent passes can branch on
    // single-mesh-vs-multi-mesh body cost (shadows + outlines).
    let skinnedMeshCount = 0;
    cloned.traverse((c) => {
      const sm = c as { isSkinnedMesh?: boolean; name?: string };
      if (sm.isSkinnedMesh && !sm.name?.endsWith('__outline')) skinnedMeshCount++;
    });
    const isMultiMeshBody = skinnedMeshCount > 1;

    // Pass 1: cast-shadow flag, toon-material override, weapon-strip.
    // Toon override is defensive: only convert StandardMaterial-likes (those
    // that have a `color` property) and skip arrays/null/already-toon — that
    // way enemies with multi-material setups don't vanish.
    //
    // PERF: Multi-mesh bodies (Cyber Knight = ~4 sub-meshes × 80 Bip01-bones)
    // would otherwise render TWICE per frame (shadow map + main pass). Skip
    // shadow casting on multi-mesh bodies — the chibi character's shadow
    // contribution is barely visible at combat distance anyway.
    const tintColor = bodyColorTint ? new Color(bodyColorTint) : null;
    cloned.traverse((c) => {
      if ((c as { isMesh?: boolean }).isMesh) {
        const mesh = c as Mesh;
        mesh.castShadow = !isMultiMeshBody;
        const old = mesh.material;
        if (old && !Array.isArray(old) && 'color' in old) {
          const std = old as MeshStandardMaterial;
          // Body-emissive override: when set (e.g. equipped lightning weapon),
          // every body sub-material gets a uniform glow regardless of original
          // emissive — that's what reads as "the whole armor is humming with energy".
          const emissive = bodyEmissive ? new Color(bodyEmissive) : std.emissive;
          const emissiveIntensity = bodyEmissive ? bodyEmissiveIntensity : std.emissiveIntensity;
          // Body colour-tint: LERP washed-out submeshes (light grey/white cloth)
          // toward the equipped armor's set/rarity colour so a Vault-Knight
          // body actually reads cobalt. Submeshes whose base material is
          // already saturated (low lightness, e.g. baked-cobalt armour panels)
          // are left alone — they're already on-palette.
          let baseColor = std.color;
          if (tintColor && std.color) {
            // Lightness gate: only LERP submeshes that are clearly washed-out
            // (cloth, undersuit). Threshold 0.42 catches mid-grey at ~50 %
            // brightness while still skipping baked-cobalt panels (#3b6db5
            // has lightness ~0.46 — borderline, but at strength=1.0 the LERP
            // toward the same colour is a no-op anyway, so safe).
            const lightness = (std.color.r + std.color.g + std.color.b) / 3;
            if (lightness > 0.42) {
              baseColor = std.color.clone().lerp(tintColor, bodyColorTintStrength);
            }
          }
          const toon = toonifyMaterial({
            color: baseColor,
            map: std.map ?? null,
            emissive,
            emissiveIntensity,
            transparent: std.transparent,
            name: std.name,
          });
          mesh.material = toon;
          ownedMaterials.push(toon);
        }
      }
      if (!stripWeapons) return;
      // Don't strip anything that lives under an attachment bone — those are
      // meshes we WANT to render (helmets/weapons added via MeshAttachment).
      if (isUnderAttachmentBone(c)) return;
      const lname = (c.name || '').toLowerCase();
      if (WEAPON_LIKE_MESH_NAMES.some((w) => lname.includes(w))) {
        c.visible = false;
      }
    });

    // Pass 2: per SkinnedMesh, add an inverted-hull outline sibling. We clone
    // the SkinnedMesh, share its skeleton (so it animates in lockstep), swap
    // in a back-side black material, and bump the scale 3% — the only faces
    // that win the depth test are the back-faces of the slightly-larger shell,
    // which read as a clean black silhouette.
    //
    // PERF: Skip the outline pass for multi-mesh bodies (>1 SkinnedMesh, e.g.
    // Cyber Knight = Robot+Garb+NakedHead+Armor) — outlines DOUBLE the skinned
    // draw count, which combined with multiple enemies + projectiles in combat
    // was causing repeated WebGL context-loss. Single-mesh bodies (KayKit,
    // Aegis) still get the silhouette.
    const outlines: SkinnedMesh[] = [];
    if (!isMultiMeshBody) cloned.traverse((c) => {
      const sm = c as SkinnedMesh;
      if (!(sm as { isSkinnedMesh?: boolean }).isSkinnedMesh) return;
      if (sm.name.endsWith('__outline')) return;  // never re-outline an outline
      const outline = sm.clone() as SkinnedMesh;
      outline.name = sm.name + '__outline';
      // Explicitly share skeleton bindings — clone() copies the references but
      // it's safer to set them again so a future Three.js change can't break it.
      outline.skeleton = sm.skeleton;
      outline.bindMatrix = sm.bindMatrix;
      outline.bindMatrixInverse = sm.bindMatrixInverse;
      const outlineMat = new MeshBasicMaterial({
        color: 0x0a0a0f,
        side: BackSide,
        depthWrite: true,
      });
      outline.material = outlineMat;
      ownedMaterials.push(outlineMat);
      // 4 % shell — readable silhouette without occluding the multi-mesh
      // procedural bodies (whose joined geometry may have internal faces that
      // confuse the depth test at higher scale-up values).
      outline.scale.copy(sm.scale).multiplyScalar(1.04);
      outline.castShadow = false;
      outline.receiveShadow = false;
      sm.parent?.add(outline);
      outlines.push(outline);
    });

    // Measure the body silhouette for procedural-attachment placement.
    // Visible skinned meshes only — strip outlines, hidden weapon stubs, and
    // any non-skinned meshes (handheld static props that would skew the bbox).
    //
    // Box3.setFromObject returns WORLD coords. By the time this useEffect
    // runs the body GLTF is already mounted inside PlayerCharacter's group
    // (which is positioned e.g. [0, -1, 0] in CharacterPortrait3D), so the
    // raw world bbox would have feetY ≈ -1 — wrong for procedural shapes
    // that render in body-local space. Transform the bbox to cloned-local
    // via cloned.matrixWorld.invert() so feetY ≈ 0 regardless of where the
    // character currently stands in the world.
    cloned.updateMatrixWorld(true);
    const worldToLocal = new Matrix4().copy(cloned.matrixWorld).invert();
    const bbox = new Box3();
    const tmp = new Box3();
    let measuredAny = false;
    cloned.traverse((c) => {
      const sm = c as { isSkinnedMesh?: boolean; visible: boolean; name: string };
      if (!sm.isSkinnedMesh) return;
      if (sm.visible === false) return;
      if (sm.name?.endsWith('__outline')) return;
      tmp.setFromObject(c);
      if (tmp.isEmpty()) return;
      tmp.applyMatrix4(worldToLocal);
      bbox.union(tmp);
      measuredAny = true;
    });
    if (measuredAny) {
      const min = bbox.min;
      const max = bbox.max;
      const bounds: BodyBounds = {
        feetY: min.y,
        headTopY: max.y,
        height: max.y - min.y,
        centerX: (min.x + max.x) / 2,
        centerZ: (min.z + max.z) / 2,
        halfWidth: (max.x - min.x) / 2,
        halfDepth: (max.z - min.z) / 2,
      };
      cloned.userData.bodyBounds = bounds;
      // Dev audit — log full structure once per body path so we can plan the
      // modular asset pipeline. Captures bone hierarchy, mesh inventory with
      // skin / vertex / material info, and the measured bbox.
      if (!loggedBoundsForPath.has(bodyPath)) {
        loggedBoundsForPath.add(bodyPath);
        const meshes: Array<{ name: string; type: string; isSkinned: boolean; vertices: number; visible: boolean; material: string }> = [];
        const bones: Array<{ name: string; depth: number; type: string }> = [];
        cloned.traverse((c) => {
          const o = c as { isMesh?: boolean; isSkinnedMesh?: boolean; isBone?: boolean; name?: string; type?: string; visible?: boolean; geometry?: { attributes?: { position?: { count?: number } } }; material?: { name?: string; type?: string } | Array<{ name?: string; type?: string }> };
          if (o.isMesh) {
            const mat = Array.isArray(o.material) ? o.material[0] : o.material;
            meshes.push({
              name: o.name ?? '(unnamed)',
              type: o.type ?? 'Mesh',
              isSkinned: !!o.isSkinnedMesh,
              vertices: o.geometry?.attributes?.position?.count ?? 0,
              visible: o.visible !== false,
              material: mat ? `${mat.type ?? '?'}${mat.name ? ' "' + mat.name + '"' : ''}` : 'none',
            });
          } else if (o.isBone) {
            // Compute depth via parent chain
            let depth = 0;
            let cur: { parent?: unknown } | null = c as { parent?: unknown };
            while (cur && (cur as { parent?: unknown }).parent && (cur as { parent?: { type?: string } }).parent !== cloned) {
              depth++;
              cur = (cur as { parent?: { parent?: unknown } }).parent ?? null;
              if (depth > 30) break;  // safety
            }
            bones.push({ name: o.name ?? '(unnamed)', depth, type: o.type ?? 'Bone' });
          }
        });
        // eslint-disable-next-line no-console
        console.log(
          `[PlayerCharacter audit] ${bodyPath}\n` +
          `  bounds: ${JSON.stringify(bounds)}\n` +
          `  meshes (${meshes.length}):\n` +
          meshes.map((m) => `    - ${m.name}  [${m.type}${m.isSkinned ? ' skinned' : ''}]  verts=${m.vertices}  vis=${m.visible}  mat=${m.material}`).join('\n') + '\n' +
          `  bones (${bones.length}):\n` +
          bones.map((b) => `    ${'  '.repeat(b.depth)}- ${b.name}`).join('\n'),
        );
      }
    }

    onReady(cloned);

    return () => {
      // Clean up our outline clones when the body re-renders or unmounts.
      for (const o of outlines) o.parent?.remove(o);
      // Dispose every material we created — releases GPU shader programs and
      // uniform buffers. Without this, every room transition leaks materials.
      for (const m of ownedMaterials) m.dispose();
    };
  }, [cloned, onReady, stripWeapons, bodyEmissive, bodyEmissiveIntensity, bodyColorTint, bodyColorTintStrength]);

  useCharacterAnimation({
    mixer,
    actions: actions as Record<string, ReturnType<typeof mixer.clipAction> | null>,
    rootObject: cloned,
    state: animation,
    onComplete: onAnimComplete,
    triggerKey,
  });

  // Heart-float: drive the Bip01_Heart bone in a continuous sine loop,
  // INDEPENDENT of body animations. Combat-state changes (LoopOnce on melee
  // etc.) freeze the body action at its last frame; we don't want the heart
  // to also freeze. Running the offset via useFrame keeps it always moving.
  //
  // Critical: we OFFSET from the bone's rest position rather than replacing
  // bone.position. The bone's local position already encodes its rest pose
  // from the GLB; overwriting wholesale would teleport the bone to (0,0,0)
  // of its parent's space → heart vanishes inside the body.
  const heartBoneRef = useRef<Object3D | null>(null);
  const heartRestRef = useRef<{ x: number; y: number; z: number } | null>(null);
  const heartFloatPhaseRef = useRef(Math.random() * Math.PI * 2);
  useEffect(() => {
    const found = ((): Object3D | null => {
      let f: Object3D | null = null;
      cloned.traverse((c) => { if (c.name === 'Bip01_Heart') f = c; });
      return f;
    })();
    heartBoneRef.current = found;
    heartRestRef.current = found
      ? { x: (found as Object3D).position.x, y: (found as Object3D).position.y, z: (found as Object3D).position.z }
      : null;
  }, [cloned]);

  useFrame((_, delta) => {
    const bone = heartBoneRef.current;
    const rest = heartRestRef.current;
    if (!bone || !rest) return;
    heartFloatPhaseRef.current += delta;
    const t = heartFloatPhaseRef.current;
    // Bip01_Heart's local Y axis = along the bone's primary axis. The bone
    // points +Z in world (head→tail along world Z), so local-Y in pose space
    // ≈ world-Z. ±2.5cm at ~0.25 Hz.
    bone.position.set(rest.x, rest.y + Math.sin(t * 1.6) * 0.025, rest.z);
  });

  return <primitive object={cloned} />;
}

interface Props {
  /** Items to render on the character — produced by getMeshLayers(equipment). */
  layers: MeshAsset[];
  /** Combat-state animation. */
  animation: CombatAnim;
  /** Optional one-shot anim callback (used for melee/ranged sequencing). */
  onAnimComplete?: () => void;
  /** World position of the character. */
  position?: [number, number, number];
  /** Y-rotation (facing direction). */
  rotationY?: number;
  /** Optional scale (e.g. 1.5 for bosses). */
  scale?: number;
  /** Path to the body glTF — defaults to Quaternius Adventurer. Set per-rarity for outfit-swap visuals. */
  bodyPath?: string;
  /** Strip built-in weapon sub-meshes from the body (Quaternius bodies often ship with pistols/etc baked in). */
  stripWeapons?: boolean;
  /** Bump to force re-trigger of the current animation (e.g. consecutive melee swings). */
  triggerKey?: number;
  /** Show a procedural crackling lightning orb at the right hand. */
  lightningInHand?: boolean;
  /** Hex colour applied as body-emissive for elemental tints (e.g. lightning yellow). */
  bodyEmissive?: string;
  /** Intensity for bodyEmissive (default 0.6). */
  bodyEmissiveIntensity?: number;
  /** Hex tint LERPed into washed-out body submeshes (set/rarity-driven palette match). */
  bodyColorTint?: string;
  /** LERP strength for bodyColorTint (default 0.6). */
  bodyColorTintStrength?: number;
}

/**
 * Modular 3D character: body + N equipment meshes mounted on bones.
 * Falls back to a primitive-built placeholder body when the GLB is missing.
 */
export function PlayerCharacter({
  layers, animation, onAnimComplete, position = [0, 0, 0], rotationY = 0, scale = 1,
  bodyPath = DEFAULT_BODY_PATH, stripWeapons = false, triggerKey = 0,
  lightningInHand = false, bodyEmissive, bodyEmissiveIntensity,
  bodyColorTint, bodyColorTintStrength,
}: Props) {
  const [bodyRoot, setBodyRoot] = useState<Object3D | null>(null);

  const placeholder = <PlaceholderBody onReady={setBodyRoot} />;

  // Locate the right-hand bone so we can portal hand-held VFX (lightning orb) into it,
  // making them follow the arm's animation instead of floating at a fixed offset.
  const handBone = useMemo<Object3D | null>(() => {
    if (!bodyRoot) return null;
    for (const name of HAND_BONE_CANDIDATES) {
      const b = bodyRoot.getObjectByName(name);
      if (b) return b;
    }
    return null;
  }, [bodyRoot]);

  // Accessory-driven VFX identity: each set's accessory has a distinct
  // signature effect even though both share the same `heart_void.glb` mesh.
  //
  //   HK Seelen-Herz (`hk_heart`, bulwark)   → soul-spark burst (lightning
  //     bolts radiating from the chest emblem). Reads as the soul-fragments
  //     trapped inside the heart leaking out.
  //   VK Sturm-Relikt (`vk_relic`, stormcaller) → crossed-blade slash pulse.
  //     The VK relic's twin-sword motif is enlarged in the GLB, and the dual
  //     wind-slashes read as those swords briefly swinging forward.
  //
  // Match by exact itemId — the older `/heart/i.test(path)` regex was a
  // mistake because BOTH meshes are named `heart_void.glb`, which made the
  // dual-slash fire for the HK accessory too (and crowded out the spark
  // identity it was supposed to have).
  const hkHeartEquipped = useMemo(
    () => layers.some((l) => l.itemId === 'hk_heart'),
    [layers],
  );
  const vkRelicEquipped = useMemo(
    () => layers.some((l) => l.itemId === 'vk_relic'),
    [layers],
  );

  return (
    <group position={position} rotation={[0, rotationY, 0]} scale={scale}>
      <GLTFErrorBoundary key={bodyPath} fallback={placeholder}>
        <Suspense fallback={placeholder}>
          <GLTFBody
            bodyPath={bodyPath}
            onReady={setBodyRoot}
            animation={animation}
            onAnimComplete={onAnimComplete}
            stripWeapons={stripWeapons}
            triggerKey={triggerKey}
            bodyEmissive={bodyEmissive}
            bodyEmissiveIntensity={bodyEmissiveIntensity}
            bodyColorTint={bodyColorTint}
            bodyColorTintStrength={bodyColorTintStrength}
          />
        </Suspense>
      </GLTFErrorBoundary>
      {layers.map((asset, i) => (
        <MeshAttachment
          key={`${asset.slot}-${i}-${asset.path}`}
          parent={bodyRoot}
          asset={asset}
          bodyPath={bodyPath}
          itemId={asset.itemId}
        />
      ))}
      {/* Lightning orb: portal into the right-hand bone if we found one (so it follows the
          wrist's animation), else fall back to the fixed-offset rendering at the
          PlayerCharacter group level. */}
      {lightningInHand && handBone && createPortal(<LightningHandOrb boneAttached />, handBone)}
      {lightningInHand && !handBone && <LightningHandOrb />}
      {/* HK Seelen-Herz signature: soul-spark bolts radiating from the
          chest. Positioned ~chest emblem level; HeartSparkBurst itself
          renders at its parent group's origin, so we wrap with a positioned
          group instead of touching the component. */}
      {hkHeartEquipped && (
        <group position={[0, 0.88, 0.42]}>
          <HeartSparkBurst />
        </group>
      )}
      {/* VK Sturm-Relikt signature: twin diagonal wind-slashes forming a
          brief X-stroke over the relic's crossed-sword motif. */}
      {vkRelicEquipped && <HeartDualSlashes />}
    </group>
  );
}
